import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, ArrowRight, ShieldCheck, Check as CheckIcon } from "lucide-react";
import { api } from "../lib/api";
import SpiderLogo from "../components/SpiderLogo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

function apiErrorText(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("This reset link is missing or invalid.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (e) {
      setErr(apiErrorText(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B1220] relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl" style={{ background: "#DC1F26" }}></div>
      <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl" style={{ background: "#0A1D4F" }}></div>

      <div className="relative max-w-md mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-spidey-red grid place-items-center">
              <SpiderLogo className="w-6 h-6" color="white" />
            </div>
            <span className="font-display text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Argent</span>
          </Link>
        </div>

        <div className="mt-16 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-lg shadow-slate-900/5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 mb-4">
            <ShieldCheck className="w-3.5 h-3.5 spidey-red" />
            Choose a new password
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100">Reset your password</h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Make it at least 8 characters — mix in numbers and symbols for extra web-strength.</p>

          {done ? (
            <div data-testid="reset-success" className="mt-6 rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 p-4 flex items-center gap-3">
              <CheckIcon className="w-5 h-5 shrink-0" />
              <div className="text-sm font-medium">Password updated. Redirecting you to log in…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-3">
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">New password</Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="new-password-input" type="password" required minLength={8} className="pl-9 rounded-xl" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Confirm password</Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="confirm-password-input" type="password" required minLength={8} className="pl-9 rounded-xl" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat it" autoComplete="new-password" />
                </div>
              </div>

              {err && (
                <div data-testid="reset-error" className="text-xs font-semibold text-rose-600 bg-rose-50 dark:bg-rose-900/30 border border-rose-100 dark:border-rose-800/60 rounded-lg px-3 py-2">{err}</div>
              )}

              <Button type="submit" disabled={busy || !token} data-testid="submit-reset-btn" className="w-full rounded-full bg-spidey-red text-white hover:opacity-90 font-bold h-12">
                {busy ? "Updating…" : "Update password"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
        </div>

        <div className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
          <Link to="/login" className="hover:underline">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
